import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RobotViewer3D, { RobotViewer3DHandle } from '../components/RobotViewer3D';
import FloatingJointInspector from '../components/FloatingJointInspector';
import { Robot, RobotJoint } from '../types';
import { fetchRobot, fetchRobotAssets, uploadThumbnail } from '../api/robots';
import { extractRobotZip, revokeBlobUrls } from '../utils/zipExtractor';

// Built-in robots with bundled assets
const BUILT_IN_ROBOTS: Record<string, { name: string; description: string; zipUrl: string }> = {
  'built-in-go2': {
    name: 'Unitree Go2',
    description: 'Quadruped robot — standing balance & locomotion',
    zipUrl: '/robots/go2.zip',
  },
};

function parseUrdfJoints(urdfContent: string): RobotJoint[] {
  const doc = new DOMParser().parseFromString(urdfContent, 'text/xml');
  const els = doc.getElementsByTagName('joint');
  const joints: RobotJoint[] = [];
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    const type = (el.getAttribute('type') || 'fixed') as RobotJoint['type'];
    const axisEl = el.getElementsByTagName('axis')[0];
    const axisParts = (axisEl?.getAttribute('xyz') || '0 0 1').split(/\s+/).map(Number);
    const limitEl = el.getElementsByTagName('limit')[0];
    const parentEl = el.getElementsByTagName('parent')[0];
    const childEl = el.getElementsByTagName('child')[0];
    joints.push({
      name: el.getAttribute('name') || '',
      type,
      axis: [axisParts[0] || 0, axisParts[1] || 0, axisParts[2] || 1] as [number, number, number],
      limits: {
        lower: parseFloat(limitEl?.getAttribute('lower') || '0'),
        upper: parseFloat(limitEl?.getAttribute('upper') || '0'),
        effort: parseFloat(limitEl?.getAttribute('effort') || '0'),
        velocity: parseFloat(limitEl?.getAttribute('velocity') || '0'),
      },
      parent_link: parentEl?.getAttribute('link') || '',
      child_link: childEl?.getAttribute('link') || '',
    });
  }
  return joints;
}

const RobotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dataLoading, setDataLoading] = useState(true);
  const [meshesReady, setMeshesReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [robot, setRobot] = useState<Robot | null>(null);
  const [urdfContent, setUrdfContent] = useState<string | undefined>();
  const [meshBlobUrls, setMeshBlobUrls] = useState<Map<string, string>>(new Map());
  const meshBlobUrlsRef = useRef<Map<string, string>>(new Map());
  const [parsedJoints, setParsedJoints] = useState<RobotJoint[]>([]);
  const [selectedJoint, setSelectedJoint] = useState<string | undefined>();
  const [jointValues, setJointValues] = useState<Record<string, number>>({});
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const viewerRef = useRef<RobotViewer3DHandle>(null);

  const articulableJoints = useMemo(
    () => parsedJoints.filter((j) => j.type === 'revolute' || j.type === 'prismatic' || j.type === 'continuous'),
    [parsedJoints],
  );

  const handleMeshesLoaded = useCallback(() => {
    setMeshesReady(true);
  }, []);

  // Auto-capture thumbnail once meshes are ready and robot data is loaded
  useEffect(() => {
    if (!meshesReady || !robot || robot.thumbnail_s3_key || !viewerRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        const blob = await viewerRef.current!.captureScreenshot();
        if (blob && !cancelled) {
          await uploadThumbnail(robot.id, blob);
          setRobot((prev) => prev ? { ...prev, thumbnail_s3_key: 'auto' } : prev);
        }
      } catch {
        // Non-fatal — thumbnail is cosmetic
      }
    })();
    return () => { cancelled = true; };
  }, [meshesReady, robot]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      revokeBlobUrls(meshBlobUrlsRef.current);
    };
  }, []);

  // Load robot data
  useEffect(() => {
    if (!id) return;

    const builtIn = BUILT_IN_ROBOTS[id];
    if (builtIn) {
      setRobot({
        id,
        name: builtIn.name,
        description: builtIn.description,
        status: 'ready',
      });
      loadBuiltInRobot(builtIn.zipUrl);
    } else {
      loadUploadedRobot(id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function loadBuiltInRobot(zipUrl: string) {
    try {
      const response = await fetch(zipUrl);
      if (!response.ok) throw new Error('Failed to fetch robot assets');
      const blob = await response.blob();
      const file = new File([blob], 'robot.zip', { type: 'application/zip' });
      const result = await extractRobotZip(file);
      setUrdfContent(result.urdfContent);
      setParsedJoints(parseUrdfJoints(result.urdfContent));
      setMeshBlobUrls(result.meshBlobUrls);
      meshBlobUrlsRef.current = result.meshBlobUrls;
      setDataLoading(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load robot');
      setDataLoading(false);
    }
  }

  async function loadUploadedRobot(robotId: string) {
    try {
      const r = await fetchRobot(robotId);
      setRobot(r);
      if (r.joints) {
        setParsedJoints(r.joints);
      }

      // Fetch URDF + mesh assets from S3
      if (r.status === 'ready' && r.urdf_s3_key) {
        const assets = await fetchRobotAssets(robotId);
        console.log('[RobotDetail] assets response:', {
          hasUrdf: !!assets.urdf,
          urdfKey: assets.urdf?.key,
          meshCount: assets.meshes.length,
          meshKeys: assets.meshes.map((m) => m.key),
        });
        if (assets.urdf) {
          const urdfResp = await fetch(assets.urdf.url);
          const urdfText = await urdfResp.text();
          if (!r.joints?.length) {
            setParsedJoints(parseUrdfJoints(urdfText));
          }

          // Build mesh blob URLs keyed by relative path — must complete BEFORE setting urdfContent
          const blobMap = new Map<string, string>();
          await Promise.all(
            assets.meshes.map(async (mesh) => {
              try {
                const resp = await fetch(mesh.url);
                const blob = await resp.blob();
                const blobUrl = URL.createObjectURL(blob);
                blobMap.set(mesh.key, blobUrl);
              } catch (e) {
                console.warn('[RobotDetail] Failed to download mesh:', mesh.key, e);
              }
            }),
          );
          console.log('[RobotDetail] meshBlobUrls keys:', Array.from(blobMap.keys()));

          // Parse a mesh ref from URDF to check alignment
          const meshRefMatch = urdfText.match(/filename="([^"]+)"/);
          if (meshRefMatch) {
            console.log('[RobotDetail] Sample URDF mesh ref:', meshRefMatch[1]);
          }

          // Set meshes first, then URDF — so the viewer sees meshes when it renders
          setMeshBlobUrls(blobMap);
          meshBlobUrlsRef.current = blobMap;
          setUrdfContent(urdfText);
        }
      }

      setDataLoading(false);
    } catch (e) {
      console.error('[RobotDetail] loadUploadedRobot error:', e);
      setError('Robot not found');
      setDataLoading(false);
    }
  }

  const showLoading = dataLoading || (!meshesReady && !!urdfContent);

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <IconButton onClick={() => navigate('/robots')} sx={{ mb: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/robots')}>
          Back to Robots
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
      }}
    >
      {/* 3D Viewer — fills remaining space */}
      <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
        {!dataLoading && (
          <RobotViewer3D
            ref={viewerRef}
            urdfContent={urdfContent}
            meshBlobUrls={meshBlobUrls}
            selectedJoint={selectedJoint}
            jointValues={jointValues}
            focusJointName={selectedJoint ?? null}
            fullScreen
            onMeshesLoaded={handleMeshesLoaded}
          />
        )}

        {/* Loading overlay */}
        {showLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20,
              backgroundColor: '#0a0a0a',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: '#EF8997' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {dataLoading ? 'Loading robot...' : 'Rendering model...'}
            </Typography>
          </Box>
        )}

        {/* Back button + robot name overlay */}
        {!showLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <IconButton
              onClick={() => navigate('/robots')}
              sx={{
                backgroundColor: 'rgba(10, 10, 10, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': { backgroundColor: 'rgba(10, 10, 10, 0.8)' },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Box
              sx={{
                backgroundColor: 'rgba(10, 10, 10, 0.6)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: 1,
                px: 2,
                py: 0.75,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {robot?.name}
              </Typography>
              {robot?.description && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {robot.description}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Solid right sidebar — joint inspector */}
      {!showLoading && (
        <FloatingJointInspector
          joints={parsedJoints}
          selectedJoint={selectedJoint}
          onSelectJoint={setSelectedJoint}
          jointValues={jointValues}
          onJointValueChange={(n, v) => setJointValues((prev) => ({ ...prev, [n]: v }))}
          collapsed={inspectorCollapsed}
          onToggleCollapse={() => setInspectorCollapsed((prev) => !prev)}
          onResetAll={() => setJointValues({})}
          articulableCount={articulableJoints.length}
        />
      )}
    </Box>
  );
};

export default RobotDetail;
